import React, { Component } from 'react';
import './BluetoothTemperaturePage.css';
import { Navbar, NavLeft, NavRight, NavTitle, Page, Link } from 'framework7-react';
import FontIcon from '../../fontIcon/FontIcon';
import Content from '../../helpers/content/Content';
import toasts from '../../../utils/toasts';
import temperatureBluetooth from '../../../utils/temperatureBluetooth';
import log from "../../../utils/log";
import logError from "../../../utils/logError";

export const BluetoothTemperaturePageRoute = { name: 'bluetooth-temperature', path: '/bluetooth-temperature/' };

export default class BluetoothTemperaturePage extends Component {
	constructor(props) {
		super(props);
		this.state = {
			bluetoothStatus: 'idle', // 'idle', 'scanning', 'connecting', 'reading', 'error'
			temperature: null,
			deviceId: null,
			deviceName: null,
			availableDevices: [],
			showDeviceList: false,
			permissionStatus: 'unknown', // 'unknown', 'granted', 'denied', 'requesting'
			errorMessage: null
		};
	}

	async componentDidMount() {
		// Check if Bluetooth is available
		if (!window.ble) {
			this.setState({ 
				bluetoothStatus: 'error',
				errorMessage: 'Bluetooth not available. Make sure you are running this on a mobile device with Cordova.'
			});
			return;
		}

		// Check if Bluetooth is enabled and permissions
		try {
			const isEnabled = await temperatureBluetooth.isEnabled();
			if (isEnabled) {
				// Check permission status
				const permissionStatus = await temperatureBluetooth.checkPermissionStatus();
				if (permissionStatus === 'granted') {
					this.setState({ permissionStatus: 'granted' });
				}
			}
		} catch (error) {
			log('Error checking Bluetooth status:', error);
		}
	}

	_requestBluetoothPermissions = async () => {
		try {
			this.setState({ 
				bluetoothStatus: 'requesting',
				errorMessage: null
			});
			
			await temperatureBluetooth.requestPermissions();
			
			this.setState({ 
				bluetoothStatus: 'idle',
				permissionStatus: 'granted'
			});
			
			log('Bluetooth permissions requested successfully');
		} catch (error) {
			logError('Bluetooth permission request error:', error);
			let errorMessage = 'Failed to request Bluetooth permissions.';
			if (error.message.includes('disabled')) {
				errorMessage = 'Bluetooth is disabled. Please enable Bluetooth in your device settings.';
			}
			this.setState({ 
				bluetoothStatus: 'error',
				errorMessage: errorMessage
			});
		}
	};

	_scanForDevices = async () => {
		try {
			this.setState({ 
				bluetoothStatus: 'scanning',
				availableDevices: [],
				showDeviceList: false,
				errorMessage: null
			});
			log('Scanning for Bluetooth devices...');
			
			const deviceIds = await temperatureBluetooth.scanAndLog({
				seconds: 10, 
				reportDuplicates: false 
			});
			
			if (deviceIds.length > 0) {
				this.setState({ 
					availableDevices: deviceIds,
					showDeviceList: true,
					bluetoothStatus: 'idle'
				});
				log('Found devices:', deviceIds);
			} else {
				this.setState({ 
					bluetoothStatus: 'error',
					errorMessage: 'No Bluetooth devices found. Make sure your device is nearby and Bluetooth is enabled.'
				});
				log('No Bluetooth devices found');
			}
		} catch (error) {
			logError('Bluetooth scan error:', error);
			this.setState({ 
				bluetoothStatus: 'error',
				errorMessage: 'Failed to scan for devices. Please check Bluetooth permissions and try again.'
			});
		}
	};

	_connectAndReadTemperature = async (deviceId) => {
		try {
			this.setState({ 
				bluetoothStatus: 'connecting',
				showDeviceList: false,
				errorMessage: null
			});
			log('Connecting to device:', deviceId);
			
			// Connect and monitor temperature (notifications or polling)
			const result = await temperatureBluetooth.connectAndSubscribeToTemperature(deviceId, (temperature) => {
				log('Temperature update received:', temperature);
				this.setState({ 
					temperature: temperature,
					deviceId: deviceId
				});
			});
			
			this.setState({ 
				bluetoothStatus: 'idle'
			});
			
			log(`Connected and monitoring temperature using ${result.method}`);
		} catch (error) {
			logError('Bluetooth connection/reading error:', error);
			this.setState({ 
				bluetoothStatus: 'error',
				errorMessage: 'Failed to connect or read temperature. Please try again.'
			});
		}
	};

	// Refresh temperature for currently connected device
	_refreshTemperature = async () => {
		try {
			if (!temperatureBluetooth.isConnected()) {
				throw new Error('No device connected. Please connect to a device first.');
			}

			this.setState({ 
				bluetoothStatus: 'reading',
				errorMessage: null
			});
			
			log('Manually refreshing temperature...');
			
			// Manually read temperature
			const deviceId = temperatureBluetooth.getConnectedDeviceId();
			const temperature = await temperatureBluetooth.readTemperature(deviceId);
			
			this.setState({ 
				bluetoothStatus: 'idle',
				temperature: temperature
			});
			
			log('Manual temperature refresh:', temperature);
		} catch (error) {
			logError('Temperature refresh error:', error);
			this.setState({ 
				bluetoothStatus: 'error',
				errorMessage: error.message || 'Failed to refresh temperature. Please try again.'
			});
		}
	};

	// Force fresh temperature reading by disconnecting and reconnecting
	_forceFreshTemperature = async () => {
		try {
			if (!temperatureBluetooth.isConnected()) {
				throw new Error('No device connected. Please connect to a device first.');
			}

			this.setState({ 
				bluetoothStatus: 'connecting',
				errorMessage: null
			});
			
			log('Forcing fresh connection...');
			const deviceId = temperatureBluetooth.getConnectedDeviceId();
			
			// Disconnect and reconnect with monitoring
			await temperatureBluetooth.disconnect();
			const result = await temperatureBluetooth.connectAndSubscribeToTemperature(deviceId, (temperature) => {
				log('Fresh temperature update received:', temperature);
				this.setState({ 
					temperature: temperature
				});
			});
			
			this.setState({ 
				bluetoothStatus: 'idle'
			});
			
			log(`Forced fresh connection using ${result.method}`);
		} catch (error) {
			logError('Force fresh temperature error:', error);
			this.setState({ 
				bluetoothStatus: 'error',
				errorMessage: error.message || 'Failed to force fresh temperature. Please try again.'
			});
		}
	};

	// Debug all sensor data
	_debugSensorData = async () => {
		try {
			if (!temperatureBluetooth.isConnected()) {
				throw new Error('No device connected. Please connect to a device first.');
			}

			this.setState({ 
				bluetoothStatus: 'reading',
				errorMessage: null
			});
			
			log('Debugging all sensor data...');
			const deviceId = temperatureBluetooth.getConnectedDeviceId();
			const allData = await temperatureBluetooth.readAllWithDebug(deviceId);
			
			// Show debug info in toast
			const debugMessage = `🔍 Debug Data:
🌡️ Temp: ${allData.temperature ? `${allData.temperature.celsius.toFixed(1)}°C` : 'N/A'}
💧 Humidity: ${allData.humidity ? `${allData.humidity.percent.toFixed(1)}%` : 'N/A'}
🔋 Battery: ${allData.battery ? `${allData.battery.percent}%` : 'N/A'}
📊 Pressure: ${allData.pressure ? `${allData.pressure.hPa.toFixed(1)} hPa` : 'N/A'}`;
			
			toasts.show(debugMessage, 6000);
			
			this.setState({ 
				bluetoothStatus: 'idle',
				temperature: allData.temperature
			});
			
			log('Debug sensor data:', allData);
		} catch (error) {
			logError('Debug sensor data error:', error);
			toasts.error(`❌ Debug failed: ${error.message}`, 3000);
			this.setState({ 
				bluetoothStatus: 'error',
				errorMessage: error.message || 'Failed to debug sensor data.'
			});
		}
	};

	_handleScanButtonClick = () => {
		this._scanForDevices();
	};

	_handleDeviceSelect = (deviceId) => {
		this._connectAndReadTemperature(deviceId);
	};

	_handleFindBestMatch = async () => {
		try {
			this.setState({ 
				bluetoothStatus: 'scanning',
				errorMessage: null
			});
			log('Finding best SensorPush device...');
			
			const result = await temperatureBluetooth.findAndConnectBestMatchWithMonitoring((temperature) => {
				log('Best match temperature update:', temperature);
				this.setState({ 
					temperature: temperature
				});
			});
			
			this.setState({ 
				bluetoothStatus: 'idle',
				deviceId: result.device.id,
				deviceName: result.device.name
			});
			
			log(`Best match result using ${result.method}:`, result);
		} catch (error) {
			logError('Find best match error:', error);
			this.setState({ 
				bluetoothStatus: 'error',
				errorMessage: error.message || 'Failed to find and connect to SensorPush device.'
			});
		}
	};

	_handleGetTemperatureToast = async () => {
		try {
			this.setState({ 
				bluetoothStatus: 'reading',
				errorMessage: null
			});
			log('Getting current temperature for toast...');
			
			let result;
			
			// If we have a connected device, read the current temperature
			if (temperatureBluetooth.isConnected()) {
				log('Device connected, reading current temperature...');
				const deviceId = temperatureBluetooth.getConnectedDeviceId();
				const temperature = await temperatureBluetooth.readTemperature(deviceId);
				
				result = {
					temperature: temperature,
					device: {
						id: deviceId,
						name: this.state.deviceName || 'Connected Device'
					}
				};
			} else {
				// If no device connected, find and connect to best match with monitoring
				log('No device connected, finding best match with monitoring...');
				const connectionResult = await temperatureBluetooth.findAndConnectBestMatchWithMonitoring((temperature) => {
					log('Toast temperature update:', temperature);
					this.setState({ 
						temperature: temperature
					});
				});
				
				// Wait for the first temperature reading
				await new Promise(resolve => setTimeout(resolve, 2000));
				
				if (this.state.temperature) {
					result = {
						temperature: this.state.temperature,
						device: {
							id: connectionResult.device.id,
							name: connectionResult.device.name
						}
					};
				} else {
					throw new Error('No temperature data received from device');
				}
			}
			
			// Show temperature in toast
			const tempText = `${result.temperature.celsius.toFixed(1)}°C / ${result.temperature.fahrenheit.toFixed(1)}°F`;
			const deviceText = result.device.name || 'Unknown Device';
			const toastMessage = `🌡️ ${tempText}\n📱 ${deviceText}`;
			
			toasts.show(toastMessage, 4000);
			
			this.setState({ 
				bluetoothStatus: 'idle',
				deviceId: result.device.id,
				deviceName: result.device.name
			});
			
			log('Temperature toast result:', result);
		} catch (error) {
			logError('Get temperature toast error:', error);
			toasts.error(`❌ Failed to get temperature: ${error.message}`, 3000);
			this.setState({ 
				bluetoothStatus: 'error',
				errorMessage: error.message || 'Failed to get temperature.'
			});
		}
	};

	_getButtonText = () => {
		switch (this.state.bluetoothStatus) {
			case 'scanning':
				return 'Scanning...';
			case 'connecting':
				return 'Connecting...';
			case 'reading':
				return 'Reading...';
			case 'requesting':
				return 'Requesting Permissions...';
			case 'error':
				return 'Try Again';
			default:
				return 'Scan for Devices';
		}
	};

	_getScanButtonDisabled = () => {
		return this.state.bluetoothStatus === 'scanning' || 
			   this.state.bluetoothStatus === 'connecting' || 
			   this.state.bluetoothStatus === 'reading' ||
			   this.state.bluetoothStatus === 'requesting';
	};

	_renderDeviceList = () => {
		if (!this.state.showDeviceList || this.state.availableDevices.length === 0) {
			return null;
		}

		return (
			<div className="device-list" style={{
				marginTop: '20px',
				padding: '15px',
				backgroundColor: '#f8f9fa',
				borderRadius: '8px',
				border: '1px solid #dee2e6'
			}}>
				<h4 style={{ margin: '0 0 15px 0', color: '#495057' }}>Available Devices:</h4>
				{this.state.availableDevices.map((deviceId, index) => (
					<div 
						key={deviceId}
						onClick={() => this._handleDeviceSelect(deviceId)}
						style={{
							padding: '12px',
							margin: '8px 0',
							backgroundColor: 'white',
							border: '1px solid #ced4da',
							borderRadius: '6px',
							cursor: 'pointer',
							transition: 'all 0.2s ease'
						}}
						onMouseEnter={(e) => e.target.style.backgroundColor = '#e9ecef'}
						onMouseLeave={(e) => e.target.style.backgroundColor = 'white'}
					>
						<div style={{ fontWeight: 'bold', color: '#495057' }}>
							Device {index + 1}
						</div>
						<div style={{ fontSize: '12px', color: '#6c757d', fontFamily: 'monospace' }}>
							{deviceId}
						</div>
						<div style={{ fontSize: '12px', color: '#28a745', marginTop: '4px' }}>
							Tap to connect and read temperature
						</div>
					</div>
				))}
			</div>
		);
	};

	_renderTemperatureDisplay = () => {
		if (!this.state.temperature) {
			return null;
		}

		return (
			<div className="temperature-display" style={{
				marginTop: '20px',
				padding: '20px',
				backgroundColor: '#d4edda',
				border: '1px solid #c3e6cb',
				borderRadius: '8px',
				textAlign: 'center'
			}}>
				<h3 style={{ margin: '0 0 15px 0', color: '#155724' }}>Temperature Reading</h3>
				<div style={{ fontSize: '24px', fontWeight: 'bold', color: '#155724', marginBottom: '10px' }}>
					{this.state.temperature.celsius.toFixed(1)}°C
				</div>
				<div style={{ fontSize: '18px', color: '#155724', marginBottom: '15px' }}>
					{this.state.temperature.fahrenheit.toFixed(1)}°F
				</div>
				{this.state.deviceName && (
					<div style={{ fontSize: '14px', color: '#155724', marginBottom: '5px' }}>
						{this.state.deviceName}
					</div>
				)}
				{this.state.deviceId && (
					<div style={{ fontSize: '12px', color: '#6c757d', fontFamily: 'monospace' }}>
						ID: {this.state.deviceId}
					</div>
				)}
				
				{/* Refresh Button */}
				<button 
					onClick={this._refreshTemperature}
					disabled={this._getScanButtonDisabled()}
					style={{
						marginTop: '15px',
						padding: '10px 20px',
						backgroundColor: this._getScanButtonDisabled() ? '#6c757d' : '#007bff',
						color: 'white',
						border: 'none',
						borderRadius: '6px',
						cursor: this._getScanButtonDisabled() ? 'not-allowed' : 'pointer',
						fontSize: '14px',
						fontWeight: 'bold',
						transition: 'background-color 0.2s ease',
						marginRight: '10px'
					}}
				>
					{this.state.bluetoothStatus === 'reading' ? 'Refreshing...' : '🔄 Refresh'}
				</button>

				{/* Force Fresh Button */}
				<button 
					onClick={this._forceFreshTemperature}
					disabled={this._getScanButtonDisabled()}
					style={{
						marginTop: '15px',
						padding: '10px 20px',
						backgroundColor: this._getScanButtonDisabled() ? '#6c757d' : '#17a2b8',
						color: 'white',
						border: 'none',
						borderRadius: '6px',
						cursor: this._getScanButtonDisabled() ? 'not-allowed' : 'pointer',
						fontSize: '14px',
						fontWeight: 'bold',
						transition: 'background-color 0.2s ease',
						marginRight: '10px'
					}}
				>
					{this.state.bluetoothStatus === 'connecting' ? 'Forcing...' : '⚡ Force Fresh'}
				</button>

				{/* Debug Button */}
				<button 
					onClick={this._debugSensorData}
					disabled={this._getScanButtonDisabled()}
					style={{
						marginTop: '15px',
						padding: '10px 20px',
						backgroundColor: this._getScanButtonDisabled() ? '#6c757d' : '#6f42c1',
						color: 'white',
						border: 'none',
						borderRadius: '6px',
						cursor: this._getScanButtonDisabled() ? 'not-allowed' : 'pointer',
						fontSize: '14px',
						fontWeight: 'bold',
						transition: 'background-color 0.2s ease'
					}}
				>
					{this.state.bluetoothStatus === 'reading' ? 'Debugging...' : '🔍 Debug'}
				</button>
				{/* Debug Button */}

			</div>
		);
	};

	_renderError = () => {
		if (!this.state.errorMessage) {
			return null;
		}

		return (
			<div className="error-message" style={{
				marginTop: '20px',
				padding: '15px',
				backgroundColor: '#f8d7da',
				border: '1px solid #f5c6cb',
				borderRadius: '8px',
				color: '#721c24'
			}}>
				<strong>Error:</strong> {this.state.errorMessage}
			</div>
		);
	};

	render() {
		return (
			<Page className="g-bluetooth-temperature-page">
				<Navbar>
					<NavLeft>
						<Link back>
							<FontIcon name="icon-arrow-left" />
						</Link>
					</NavLeft>
					<NavTitle>Bluetooth Temperature</NavTitle>
					<NavRight></NavRight>
				</Navbar>

				<Content>
					<div style={{ padding: '20px' }}>
						<h2 style={{ margin: '0 0 20px 0', color: '#495057' }}>
							Bluetooth Temperature Monitor
						</h2>
						
						<p style={{ color: '#6c757d', marginBottom: '20px' }}>
							First, ensure Bluetooth is enabled, then scan for nearby Bluetooth temperature sensors and read their current temperature.
						</p>
						
						<div style={{ 
							backgroundColor: '#fff3cd', 
							border: '1px solid #ffeaa7', 
							borderRadius: '8px', 
							padding: '15px', 
							marginBottom: '20px',
							fontSize: '14px'
						}}>
							<strong>Permission Issue:</strong> The "Allow LogRx to find Bluetooth devices?" prompt only appears on the FIRST app launch. If you've used the app before, the permission decision has already been made.
							<br/><br/>
							<strong>If the prompt never appeared (even on fresh install):</strong>
							<br/>
							• Check <strong>Settings → Privacy & Security → Bluetooth</strong> to see if LogRx is listed
							<br/>
							• If LogRx is listed, permissions were already granted/denied
							<br/>
							• If LogRx is NOT listed, the permission request failed
							<br/><br/>
							<strong>Troubleshooting:</strong>
							<br/>
							• Try <strong>restarting the app</strong> completely
							<br/>
							• Check console logs for BLE debugging information
							<br/>
							• Ensure Bluetooth is enabled in device settings
						</div>
						<button
							onClick={temperatureBluetooth.triggerAndReadTemp}
							style={{
								marginTop: '15px',
								marginBottom: '15px',
								padding: '10px 20px',
								color: 'white',
								border: 'none',
								borderRadius: '6px',
								fontSize: '14px',
								fontWeight: 'bold',
								transition: 'background-color 0.2s ease'
							}}
						>
							new write and read
						</button>

						{/* Permission Button */}
						<button 
							onClick={this._requestBluetoothPermissions}
							disabled={this.state.permissionStatus === 'granted' || this._getScanButtonDisabled()}
							style={{
								padding: '15px 30px',
								backgroundColor: this.state.permissionStatus === 'granted' ? '#28a745' : '#dc3545',
								color: 'white',
								border: 'none',
								borderRadius: '8px',
								cursor: this.state.permissionStatus === 'granted' ? 'default' : 'pointer',
								fontSize: '16px',
								fontWeight: 'bold',
								width: '100%',
								marginBottom: '15px',
								transition: 'background-color 0.2s ease'
							}}
						>
							{this.state.permissionStatus === 'granted' ? '✅ Permissions Granted' : '🔐 Request Bluetooth Permissions'}
						</button>

						{/* Scan Button */}
						<button 
							onClick={this._handleScanButtonClick}
							disabled={this._getScanButtonDisabled()}
							style={{
								padding: '15px 30px',
								backgroundColor: this._getScanButtonDisabled() ? '#6c757d' : '#007bff',
								color: 'white',
								border: 'none',
								borderRadius: '8px',
								cursor: this._getScanButtonDisabled() ? 'not-allowed' : 'pointer',
								fontSize: '16px',
								fontWeight: 'bold',
								width: '100%',
								marginBottom: '15px',
								transition: 'background-color 0.2s ease'
							}}
						>
							{this._getButtonText()}
						</button>

						{/* Find Best Match Button */}
						<button 
							onClick={this._handleFindBestMatch}
							disabled={this._getScanButtonDisabled()}
							style={{
								padding: '15px 30px',
								backgroundColor: this._getScanButtonDisabled() ? '#6c757d' : '#28a745',
								color: 'white',
								border: 'none',
								borderRadius: '8px',
								cursor: this._getScanButtonDisabled() ? 'not-allowed' : 'pointer',
								fontSize: '16px',
								fontWeight: 'bold',
								width: '100%',
								marginBottom: '15px',
								transition: 'background-color 0.2s ease'
							}}
						>
							{this.state.bluetoothStatus === 'scanning' ? 'Finding Best Device...' : 'Find Best SensorPush Device'}
						</button>

						{/* Get Temperature Toast Button */}
						<button 
							onClick={this._handleGetTemperatureToast}
							disabled={this._getScanButtonDisabled()}
							style={{
								padding: '15px 30px',
								backgroundColor: this._getScanButtonDisabled() ? '#6c757d' : '#ffc107',
								color: 'white',
								border: 'none',
								borderRadius: '8px',
								cursor: this._getScanButtonDisabled() ? 'not-allowed' : 'pointer',
								fontSize: '16px',
								fontWeight: 'bold',
								width: '100%',
								transition: 'background-color 0.2s ease'
							}}
						>
							{this.state.bluetoothStatus === 'reading' ? 'Getting Temperature...' : '🌡️ Get Current Temperature Toast'}
						</button>

						{/* Force Fresh Temperature Button */}
						<button 
							onClick={this._forceFreshTemperature}
							disabled={this._getScanButtonDisabled()}
							style={{
								padding: '15px 30px',
								backgroundColor: this._getScanButtonDisabled() ? '#6c757d' : '#17a2b8',
								color: 'white',
								border: 'none',
								borderRadius: '8px',
								cursor: this._getScanButtonDisabled() ? 'not-allowed' : 'pointer',
								fontSize: '16px',
								fontWeight: 'bold',
								width: '100%',
								marginBottom: '15px',
								transition: 'background-color 0.2s ease'
							}}
						>
							{this.state.bluetoothStatus === 'reading' ? 'Forcing Fresh...' : 'Force Fresh Temperature'}
						</button>

						{/* Debug Sensor Data Button */}
						<button 
							onClick={this._debugSensorData}
							disabled={this._getScanButtonDisabled()}
							style={{
								padding: '15px 30px',
								backgroundColor: this._getScanButtonDisabled() ? '#6c757d' : '#6f42c1',
								color: 'white',
								border: 'none',
								borderRadius: '8px',
								cursor: this._getScanButtonDisabled() ? 'not-allowed' : 'pointer',
								fontSize: '16px',
								fontWeight: 'bold',
								width: '100%',
								marginBottom: '15px',
								transition: 'background-color 0.2s ease'
							}}
						>
							{this.state.bluetoothStatus === 'reading' ? 'Debugging...' : 'Debug Sensor Data'}
						</button>

						{this._renderDeviceList()}
						{this._renderTemperatureDisplay()}
						{this._renderError()}

						{this.state.bluetoothStatus === 'scanning' && (
							<div style={{ 
								marginTop: '20px', 
								textAlign: 'center',
								color: '#6c757d'
							}}>
								<div>Scanning for devices...</div>
								<div style={{ fontSize: '12px', marginTop: '5px' }}>
									This may take up to 10 seconds
								</div>
							</div>
						)}
					</div>
				</Content>
			</Page>
		);
	}
}
